/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  SPORTS_PAY_FIELD_HINTS,
  SPORTS_PAY_LANGUAGES,
} from '@sportninja/common/features/account/sportsPaySignUp'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import Text from 'src/components/ts-components/Text'
import { LABEL_LETTER_SPACING, SECTION_LETTER_SPACING } from '../constants'
import {
  PhoneField,
  ProvinceField,
  SelectField,
  TextField,
} from './FormikField'

/** Section heading + its fields. */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div css={sectionStyle}>
    <Text variant='captionLarge' weight='semiBold' color={colors.NEUTRAL_50}>
      {title}
    </Text>
    {children}
  </div>
)

/**
 * SportsPay affiliate application fields.
 *
 * Pure view — all state lives in Formik and all validation is the server's
 * (errors arrive via `applySportsPayApiError`). Asterisks are omitted
 * deliberately: the whole body is optional, since every field falls back to the
 * organization's account, and anything supplied simply overrides that.
 *
 * SportsPay collects the authorized signor's details, banking information and
 * the legal agreements on their own side, through the application they send
 * out, so none of that appears here.
 */
const SportsPaySignupForm: React.FC = () => {
  return (
    <div css={formStyle}>
      <Section title='ORGANIZATION INFORMATION'>
        {/* Long labels on both sides — stacks earlier than the other rows. */}
        <div css={twoColumnLongLabelStyle}>
          <TextField
            name='legalName'
            label={capitalizeWords("Organization's Legal Name")}
            hint={SPORTS_PAY_FIELD_HINTS.legalName}
            autoComplete='organization'
            placeholder={capitalizeWords("Enter Organization's Legal Name")}
          />
          <TextField
            name='orgEmail'
            label={capitalizeWords("Organization's Main E-mail Address")}
            placeholder={capitalizeWords(
              "Enter Organization's Main E-mail Address"
            )}
          />
        </div>
      </Section>

      <div css={dividerStyle} />

      <Section title='CONTACT INFORMATION'>
        <div css={twoColumnStyle}>
          <TextField
            name='addressLine1'
            label={capitalizeWords('Address Line 1')}
            hint={SPORTS_PAY_FIELD_HINTS.addressLine1}
            placeholder={capitalizeWords('Enter Address Line 1')}
          />
          <TextField
            name='addressLine2'
            label={capitalizeWords('Address Line 2')}
            placeholder={capitalizeWords('Enter Address Line 2')}
          />
        </div>

        <div css={threeColumnStyle}>
          <TextField
            name='city'
            label={capitalizeWords('City')}
            placeholder={capitalizeWords('Enter City')}
          />
          {/* The picker holds a province id; `provinceCode` carries the ISO
              code SportsPay actually wants. */}
          <ProvinceField
            name='province'
            codeName='provinceCode'
            label={capitalizeWords('Province')}
            placeholder={capitalizeWords('Please Select')}
          />
          <TextField
            name='postalCode'
            label={capitalizeWords('Postal Code')}
            placeholder={capitalizeWords('Enter Postal Code')}
          />
        </div>

        <div css={twoColumnStyle}>
          <TextField
            name='websiteAddress'
            label={capitalizeWords('Website Address')}
            hint={SPORTS_PAY_FIELD_HINTS.websiteAddress}
            autoComplete='url'
            placeholder={capitalizeWords('Enter Website Address')}
          />
          <TextField
            name='mainContactPerson'
            label={capitalizeWords('Main Contact Person')}
            hint={SPORTS_PAY_FIELD_HINTS.mainContactPerson}
            autoComplete='name'
            placeholder={capitalizeWords('Enter Main Contact Person')}
          />
        </div>

        <div css={twoColumnStyle}>
          <PhoneField
            name='orgPhone'
            label={capitalizeWords("Organization's Main Phone Number")}
            hint={SPORTS_PAY_FIELD_HINTS.phone}
          />
        </div>
      </Section>

      <div css={dividerStyle} />

      <Section title='BUSINESS INFORMATION'>
        <div css={twoColumnStyle}>
          <TextField
            name='businessNumber'
            label={capitalizeWords('Business Number')}
            hint={SPORTS_PAY_FIELD_HINTS.businessNumber}
            placeholder={capitalizeWords('Enter Business Number')}
          />
          <TextField
            name='governingBody'
            label={capitalizeWords('Governing Body')}
            placeholder={capitalizeWords('Enter Governing Body')}
          />
        </div>

        <div css={twoColumnStyle}>
          <SelectField
            name='language'
            label={capitalizeWords('Language')}
            options={SPORTS_PAY_LANGUAGES}
            placeholder={capitalizeWords('Please Select')}
          />
        </div>
      </Section>
    </div>
  )
}

export default SportsPaySignupForm

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/**
 * Field labels are rendered inside the shared inputs as a Text (<p>) with no
 * styling hook, so the tracking is applied here as a descendant rule scoped to
 * this form rather than by widening those shared components.
 *
 * Both rules live here on purpose. The section headings are `> div > p`, so
 * that selector out-specifies the bare `p` rule and gives them the wider
 * tracking. Setting it on the heading's own `Text` instead would lose — a
 * descendant selector beats a single element class.
 */
const formStyle = css`
  /* Establishes the container the row style below measures against. */
  container-type: inline-size;

  /* When an API error scrolls a field into view, keep it clear of the sticky
     section heading above it rather than flush against the viewport edge. */
  * {
    scroll-margin-top: 16px;
    scroll-margin-bottom: 16px;
  }

  p {
    ${LABEL_LETTER_SPACING}
  }

  > div > p {
    ${SECTION_LETTER_SPACING}
  }
`

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`

const dividerStyle = css`
  height: 1px;
  background-color: ${colors.SECONDARY_100};
  margin-bottom: 16px;
`

const twoColumnStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

/**
 * Two columns that stack EARLIER than the rest of the form.
 *
 * Only for rows whose labels are long enough to wrap while the row is still
 * two-up — today that is Legal Name + Main E-mail Address. A wrapped label
 * pushes its own input down while its neighbour's stays put, so the two
 * controls no longer line up.
 *
 * Measured against the FORM PANEL, not the viewport: the panel is ~2/3 of a
 * 72vw modal, so a viewport media query fires far too late — at a 1200px window
 * the panel is already ~575px and the labels have wrapped.
 */
const COLLAPSE_LONG_LABELS_AT_PX = 560

const twoColumnLongLabelStyle = css`
  ${twoColumnStyle}

  @container (max-width: ${COLLAPSE_LONG_LABELS_AT_PX}px) {
    grid-template-columns: 1fr;
  }
`

const threeColumnStyle = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`
